/** Renders admin-created custom CMS sections on public pages. */
import React from 'react';
import { useCMS } from '../hooks/useCMS';

const isImg = (k) => /image|logo|photo|favicon/.test(k);
const isVid = (k) => /video/.test(k);
const isLink = (k) => /link|url/.test(k);
const isBtn = (k) => /^(cta|button)/.test(k) && !isLink(k);

export default function CMSCustomSections({ pageId, dark = false }) {
  const { sections } = useCMS(pageId);
  const customs = Object.entries(sections || {}).filter(([, v]) => v && typeof v === 'object' && v._custom);
  if (customs.length === 0) return null;

  return (
    <div data-testid={`cms-custom-sections-${pageId}`}>
      {customs.map(([sid, sec]) => {
        const fields = Object.entries(sec).filter(([k, v]) => !k.startsWith('_') && v !== '' && v != null);
        const images = fields.filter(([k]) => isImg(k));
        const videos = fields.filter(([k]) => isVid(k) && !isImg(k));
        const buttons = fields.filter(([k]) => isBtn(k));
        const link = (fields.find(([k]) => isLink(k)) || [])[1];
        const texts = fields.filter(([k]) => !isImg(k) && !isVid(k) && !isBtn(k) && !isLink(k));
        return (
          <section key={sid} className={`py-14 px-6 ${dark ? 'bg-black text-white' : 'bg-white text-gray-900'}`} data-testid={`cms-section-${sid}`}>
            <div className="max-w-5xl mx-auto">
              {sec._name && (
                <>
                  <p className="text-[10px] uppercase tracking-[0.3em] text-red-600 font-bold mb-2">ACE</p>
                  <h2 className={`text-2xl md:text-3xl font-black mb-6 ${dark ? 'text-white' : 'text-gray-900'}`}>{sec._name}</h2>
                </>
              )}
              <div className={`grid gap-8 ${(images.length || videos.length) && texts.length ? 'md:grid-cols-2 items-start' : ''}`}>
                {texts.length > 0 && (
                  <div className="space-y-3">
                    {texts.map(([k, v]) => (
                      /price|rate|cost/.test(k)
                        ? <p key={k} className="text-3xl font-black text-red-600">{String(v)}</p>
                        : String(v).length < 60 && /title|heading/.test(k)
                          ? <h3 key={k} className="text-xl font-bold">{String(v)}</h3>
                          : <div key={k} className={`text-sm leading-relaxed ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                              {String(v).split('\n').map((line, i) => <p key={i} className="mb-2">{line}</p>)}
                            </div>
                    ))}
                    {buttons.map(([k, v]) => (
                      <a key={k} href={link || '/booking'}
                        className="inline-block bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-widest px-6 py-3 rounded-full transition-colors"
                        data-testid={`cms-section-${sid}-cta`}>{String(v)}</a>
                    ))}
                  </div>
                )}
                {(images.length > 0 || videos.length > 0) && (
                  <div className="space-y-4">
                    {images.map(([k, v]) => (
                      <img key={k} src={String(v)} alt={sec._name || sid} className="w-full rounded-xl object-cover max-h-[420px]" loading="lazy" />
                    ))}
                    {videos.map(([k, v]) => (
                      <video key={k} src={String(v)} className="w-full rounded-xl max-h-[420px]" controls playsInline preload="metadata" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
